import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { connect, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import StandardTableToolbar from '../../../../shared/components/table-toolbar/StandardTableToolbar';
import TableComponent from './TableComponent';
import {
  getAppointmentsByParent,
  cancelAppointment,
  removeAppointment,
} from '../../../../redux/actions/appointmentAction';
import MessageModal from '../../../../shared/components/Modals/MessageModal';
import Pagination from '../../../../shared/components/pagination/Pagination';
import config from '../../../../config/app.config';
import withRouter from '../../../../shared/components/withRouter';
import { useParentProfileAppointmentsHeaders } from './AppointmentsConstants';
import { useAppointmentLabels } from '../../../App/customize/hooks/useAppointmentLabels';
import { useGetFacilityServices } from '../../../App/customize/hooks/useGetFacilityServices';
import { useAppointmentStatusesArchiveModal } from '../../../modals/AppointmentStatusesArchive/AppointmentStatusesArchive.hooks';
import { getProfileAppointmentsSortOptions } from '../../Appointments/appointmentsToolbarConfig';

const PetAppointments = ({
  getAppointmentsByParent,
  cancelAppointment,
  removeAppointment,
  parentId,
}) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const petId = searchParams.get('pet');

  const rows = useSelector(
    (state) => state.appointments.appointmentsByParent?.data ?? [],
  );
  const total = useSelector(
    (state) => state.appointments.appointmentsByParent?.total,
  );
  const facilityUtcOffset = useSelector(
    (state) => state.user?.profile?.facility?.utcOffset,
  );
  const { data: services } = useGetFacilityServices();
  const isBoardingOnly = !!services?.isBoardingOnly;

  const [dateView, setDateView] = useState('past');
  const [sortOrder, setSortOrder] = useState(-1);
  const [sortField, setSortField] = useState('dateTime');
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(config.DEFAULT_PAGINATION_LENGTH);
  const [responseBox, setResponseBox] = useState({
    showResponse: false,
    responseType: null,
    responseAlertType: null,
    responseFooter: false,
    message: null,
  });
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [confirmType, setConfirmType] = useState(null);

const futureView = dateView === 'future';
const profileUhVariantClass =
  futureView && isBoardingOnly
    ? 'profile-uh-variant--boarding-future'
    : futureView
      ? 'profile-uh-variant--vet-future'
      : isBoardingOnly
        ? 'profile-uh-variant--boarding-past'
        : 'profile-uh-variant--vet-past';
const labels = useAppointmentLabels();
const heads = useParentProfileAppointmentsHeaders({ futureView });
  const sortOptions = useMemo(
    () => getProfileAppointmentsSortOptions({ futureView, petId }),
    [futureView, petId],
  );

  const { openAppointmentStatusesArchiveModal } =
    useAppointmentStatusesArchiveModal();

  const getAppointments = useCallback(() => {
  getAppointmentsByParent({
    id: parentId,
    sort: {
      field: sortField,
      order: sortOrder,
    },
    dateView,
    ...(futureView
      ? {}
      : {
          status: [
            config.appointmentStatus.upcoming,
            config.appointmentStatus.ongoing,
            config.appointmentStatus.past,
            config.appointmentStatus.no_updates,
          ],
        }),
    petNameFilter: petId ? [petId] : [],
    limit,
    offset,
  });
  }, [
    dateView,
    futureView,
    getAppointmentsByParent,
    limit,
    offset,
    parentId,
    petId,
    sortField,
    sortOrder,
  ]);

  useEffect(() => {
    getAppointments();
  }, [getAppointments]);

  useEffect(() => {
    if (petId && sortField === 'petName') {
      setSortField('dateTime');
      setSortOrder(futureView ? 1 : -1);
      setOffset(0);
    }
  }, [petId, sortField, futureView]);

  const handleDateViewToggle = () => {
    const nextView = futureView ? 'past' : 'future';
    setDateView(nextView);
    setSortField('dateTime');
    setSortOrder(nextView === 'future' ? 1 : -1);
    setOffset(0);
  };

  const handleSort = ({ field, order }) => {
    setSortField(field ?? sortField);
    setSortOrder(order ?? sortOrder);
    setOffset(0);
  };

  const handleActions = (type, data) => {
    if (type === config.appointmentActions.history) {
      return openAppointmentStatusesArchiveModal({
        appointmentId: data.id,
        typeTitle: data.typeTitle || data.typeOther || '',
        doctor: data.doctor || '',
      });
    }

    if (type === config.appointmentActions.reschedule) {
      if (data.parentStatus === 'inactive') {
        setResponseBox({
          showResponse: true,
          responseType: 'alert',
          responseAlertType: 'danger',
          responseFooter: true,
          message: `${data.parentName} is inactive. Please activate parent to reschedule appointment.`,
        });
      } else {
        navigate(`/appointments/${data.id}`);
      }
      return;
    }

    let message = '';
    let nextConfirmType = null;

    if (type === config.appointmentActions.remove) {
      message = 'Are you sure you want to remove this appointment?';
      nextConfirmType = 'remove';
    } else if (type === config.appointmentActions.cancel) {
      message = 'Are you sure you want to cancel this appointment?';
      nextConfirmType = 'cancel';
    }

    setSelectedRowId(data);
    setConfirmType(nextConfirmType);
    setResponseBox({
      showResponse: true,
      responseType: 'confirm',
      responseAlertType: null,
      responseFooter: true,
      message,
    });
  };

  const onSuccess = async () => {
    if (confirmType === 'cancel') {
      await cancelAppointment({ appointmentId: selectedRowId });
      await getAppointments();
    }
    if (confirmType === 'remove') {
      await removeAppointment({ appointmentId: selectedRowId });
      await getAppointments();
    }
    onClose();
  };

  const onClose = () => {
    setResponseBox({
      showResponse: false,
      responseType: null,
      responseAlertType: null,
      responseFooter: false,
      message: null,
    });
  };

  const handlePageLimit = (newLimit) => {
    setLimit(newLimit);
    setOffset(0);
  };

  const nextPage = () => {
    setOffset((prevOffset) => prevOffset + limit);
  };

  const prevPage = () => {
    setOffset((prevOffset) => prevOffset - limit);
  };

  const gotoPage = (page) => {
    setOffset(limit * page);
  };

  return (
    <>
      <MessageModal
        show={responseBox.showResponse}
        type={responseBox.responseType}
        alertType={responseBox.responseAlertType}
        footer={responseBox.responseFooter}
        onSuccess={onSuccess}
        onClose={onClose}
        message={responseBox.message}
      />
<div
  className={classNames(
    'profile-appointments',
    'profile-appointments--uh',
    profileUhVariantClass,
  )}
>
        <div className="profile-appointments-toolbar-row">
          <StandardTableToolbar
            sortField={sortField}
            sortOrder={sortOrder}
            onSortChange={handleSort}
            sortOptions={sortOptions}
            filters={{}}
            filterCategories={[]}
            onFilterChange={() => {}}
            onClearAllFilters={() => {}}
            onSearchChange={() => {}}
            dateShortcuts={[]}
            onTimeFilterChange={() => {}}
          />
          <button
            type="button"
            className="btn btn-utility profile-appointments-future-btn"
            aria-pressed={futureView}
            onClick={handleDateViewToggle}
            style={{
              flex: '0 0 auto',
              width: 'auto',
              marginBottom: 0,
              fontWeight: futureView ? 700 : 400,
              borderColor: futureView ? '#9a9a9a' : undefined,
            }}
          >
            Future
          </button>
        </div>
        <TableComponent
          overflowX={false}
          heads={heads}
          rows={rows}
          sortField={sortField}
          sortOrder={sortOrder}
          handleActions={handleActions}
          handleGridSort={() => {}}
          handleGridFilter={() => {}}
          labels={{ ...labels, noAppointments: 'No updates to display' }}
          isBoardingOnly={isBoardingOnly}
          facilityUtcOffset={facilityUtcOffset}
          showActions={false}
          updatesColumnMode={!futureView}
        />
        <Pagination
          handlePageLimit={handlePageLimit}
          nextPage={nextPage}
          prevPage={prevPage}
          gotoPage={gotoPage}
          limit={limit}
          offset={offset}
          total={total}
        />
      </div>
    </>
  );
};

const mapStateToProps = (state) => ({
  appointments: state.appointments,
});

const mapDispatchToProps = {
  getAppointmentsByParent,
  cancelAppointment,
  removeAppointment,
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(PetAppointments),
);
